'use client';

import { useEffect, useRef } from 'react';
import { authenticatedFetch } from '@/lib/auth-utils';
import { hasPreviewAccessClient } from '@/lib/wsk-visibility';

/**
 * usePreviewAutoGrant — identity-based WS-K preview auto-grant for the admin shell.
 *
 * Called from the admin layout, where the shell already knows the signed-in user is staff. When the
 * caller is staff AND the readable `wsk_preview_ui` cookie is ABSENT, this POSTs `/api/preview/grant`
 * ONCE (fire-and-forget, errors swallowed) so the operator's browser — desktop or phone — gets the
 * preview cookies with no env var / secret URL. The existing WS-K nav-link reveal (which reads that
 * same cookie) is the visual confirmation; this builds no new UI.
 *
 * Idempotent + non-blocking: it fires at most once per mount and never when the cookie is already
 * present (no polling, no repeat calls), and any failure is caught so it can never disrupt admin
 * rendering. Preview is a convenience — its safe state is "not granted".
 *
 * @param isStaff whether the signed-in user is admin/owner (the same allow-list the grant route enforces).
 */
export function usePreviewAutoGrant(isStaff: boolean): void {
  const attempted = useRef(false);

  useEffect(() => {
    if (!isStaff) return;
    if (attempted.current) return;
    // Cookie already present → this browser is already previewing; do nothing.
    if (hasPreviewAccessClient()) return;

    attempted.current = true;
    void authenticatedFetch('/api/preview/grant', { method: 'POST' }).catch(() => {
      // Best-effort: a failed grant must never break the admin shell. The break-glass /preview?key=
      // path and a page reload both remain available.
    });
  }, [isStaff]);
}
